/**
 * A shared, live answer from one endpoint, for every component of the page that shows it (the notifications bell, the
 * app-wide banner, the sidebar's Activity badge): one request per interval whoever asks, never two at once.
 *
 * - Polled every `intervalMs` while someone uses it (`acquire` / `release`, counted) and the tab is visible; a hidden
 *   tab asks nothing, and coming back to it (visibility or window focus) asks at once.
 * - On an error the last answer stays and the next try waits twice as long each time, up to `maxBackoffMs`.
 * - Listeners hear only real changes (`equal`, by default the same JSON), so a poll with the same answer re-renders
 *   nothing.
 * - Signed out (`setLivePollSignedIn(false)`, from App.jsx): every poll stops and forgets its answer.
 * - `haltWhen(answer)` true: the poll stops asking by itself (no timer, no ask on focus or a new user) and keeps that
 *   answer, until someone calls `refresh()` (e.g. a service that is not reachable: asking again every interval only
 *   fills the console with failed requests).
 *
 * There is no person-wide live socket to vfs-service (presence sockets are per project), hence the poll.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { isUserActive, USER_RETURN_EVENT } from './userActivity';

export const LIVE_POLL_MS = 45000;
export const LIVE_POLL_MAX_BACKOFF_MS = 5 * 60000;
// Focus and visibility often fire together; a second one within this gap asks nothing.
const FOCUS_GAP_MS = 5000;

let signedIn = true;
const polls = new Set();

const sameJson = (a, b) => {
  if (a === b) { return true; }
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
};
// Visible and the person at the screen (a forgotten tab keeps nothing awake).
const isVisible = () => (typeof document === 'undefined' || document.visibilityState !== 'hidden') && isUserActive();

export function createLivePoll({ load, intervalMs = LIVE_POLL_MS, maxBackoffMs = LIVE_POLL_MAX_BACKOFF_MS, equal = sameJson, onClear, haltWhen = null } = {}) {
  let state = null;
  // Stopped by `haltWhen` until an explicit refresh().
  let halted = false;
  let users = 0;
  let timer = null;
  let inFlight = null;
  let failures = 0;
  let lastAt = 0;
  // Bumped when the poll is cleared, so an answer still on its way after a sign out is dropped.
  let generation = 0;
  const listeners = new Set();

  const emit = () => listeners.forEach((listener) => {
    try { listener(); } catch { /* a listener never breaks the others */ }
  });
  const delay = () => (failures ? Math.min(intervalMs * 2 ** failures, maxBackoffMs) : intervalMs);
  const stopTimer = () => { clearTimeout(timer); timer = null; };

  function schedule() {
    stopTimer();
    if (users > 0 && signedIn && !halted && isVisible()) { timer = setTimeout(() => { timer = null; refresh(); }, delay()); }
  }

  /** Sets the answer (and tells the listeners) only when it changed. */
  function set(next) {
    if (equal(state, next)) { return; }
    state = next;
    emit();
  }

  /** Asks now (or joins the request on its way); resolves to the answer. An explicit call resumes a halted poll. */
  function refresh() {
    if (!signedIn) { return Promise.resolve(state); }
    if (inFlight) { return inFlight; }
    halted = false;
    const mine = generation;
    lastAt = Date.now();
    stopTimer();
    inFlight = Promise.resolve()
      .then(load)
      .then((next) => {
        if (mine !== generation) { return; }
        failures = 0;
        if (haltWhen) {
          try { halted = Boolean(haltWhen(next)); } catch { halted = false; }
        }
        set(next);
      }, () => {
        if (mine === generation) { failures += 1; } // keep the last answer
      })
      .then(() => {
        if (mine === generation) { inFlight = null; schedule(); }
        return state;
      });
    return inFlight;
  }

  // Back to the tab: ask at once, unless it just asked (or is still waiting out an error's back-off).
  function onWake() {
    if (!isVisible()) { stopTimer(); return; }
    if (!signedIn || inFlight || halted) { return; }
    if (Date.now() - lastAt >= (failures ? delay() : FOCUS_GAP_MS)) { refresh(); } else if (!timer) { schedule(); }
  }

  function listen(on) {
    if (typeof document !== 'undefined') { document[on ? 'addEventListener' : 'removeEventListener']('visibilitychange', onWake); }
    if (typeof window !== 'undefined') {
      window[on ? 'addEventListener' : 'removeEventListener']('focus', onWake);
      window[on ? 'addEventListener' : 'removeEventListener'](USER_RETURN_EVENT, onWake);
    }
  }

  function clear() {
    generation += 1;
    stopTimer();
    inFlight = null;
    failures = 0;
    lastAt = 0;
    halted = false;
    onClear?.();
    if (state !== null) { state = null; emit(); }
  }

  const poll = {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    acquire() {
      users += 1;
      if (users === 1) {
        listen(true);
        if (!halted) { refresh(); }
      }
    },
    release() {
      users = Math.max(0, users - 1);
      if (users === 0) { listen(false); stopTimer(); }
    },
    refresh,
    set,
    /** `fn(current)` -> the next answer (an optimistic change); nothing when there is no answer yet. */
    update(fn) {
      if (state !== null) { set(fn(state)); }
    },
    signedOut: clear,
    signedIn() { if (users > 0) { refresh(); } },
    /** Whether `haltWhen` stopped it (until the next explicit refresh). */
    halted: () => halted,
    /** Forgets it for good (a poll made for one component, see useLiveRefresh). */
    dispose() {
      poll.reset();
      polls.delete(poll);
    },
    /** Tests only: forget everything, users included. */
    reset() {
      clear();
      if (users > 0) { listen(false); }
      users = 0;
      listeners.clear();
    },
  };
  polls.add(poll);
  return poll;
}

/** App.jsx: the person signed in or out. Signed out, every poll stops and forgets its answer; signed in, they ask. */
export function setLivePollSignedIn(next) {
  const value = Boolean(next);
  if (value === signedIn) { return; }
  signedIn = value;
  polls.forEach((poll) => (value ? poll.signedIn() : poll.signedOut()));
}

/** Tests only: every poll forgets everything; signed in again. */
export function resetLivePolls() {
  signedIn = true;
  polls.forEach((poll) => poll.reset());
}

const noopSubscribe = () => () => {};
const nothing = () => null;

/** The poll's answer (null until the first one); `enabled` false: this caller neither reads nor keeps it running. */
export function useLivePoll(poll, { enabled = true } = {}) {
  const answer = useSyncExternalStore(enabled ? poll.subscribe : noopSubscribe, enabled ? poll.getSnapshot : nothing, enabled ? poll.getSnapshot : nothing);
  useEffect(() => {
    if (!enabled) { return undefined; }
    poll.acquire();
    return () => poll.release();
  }, [poll, enabled]);
  return answer;
}

/**
 * Runs `onTick` (async) every `intervalMs` while the page is visible, with the same rules as every live poll here: a
 * hidden tab asks nothing and coming back asks at once, a failure waits twice as long each time, signed out it stops.
 * Not at mount (the caller loads its data itself then). Returns `{ refresh }`: run it now (a Refresh button), resolving
 * when it is done. For a page's own data (the project Home) rather than one answer shared by the whole app.
 */
export function useLiveRefresh(onTick, { intervalMs = LIVE_POLL_MS, enabled = true } = {}) {
  const tickRef = useRef(onTick);
  tickRef.current = onTick;
  const pollRef = useRef(null);
  useEffect(() => {
    if (!enabled) { return undefined; }
    let first = true;
    const poll = createLivePoll({
      intervalMs,
      // Every tick is news (the data it reloads is the caller's): never skipped as "the same answer".
      equal: () => false,
      load: async() => {
        if (first) { first = false; return 0; }
        await tickRef.current?.();
        return Date.now();
      },
    });
    pollRef.current = poll;
    poll.acquire();
    return () => {
      poll.release();
      poll.dispose();
      if (pollRef.current === poll) { pollRef.current = null; }
    };
  }, [enabled, intervalMs]);
  return useMemo(() => ({
    refresh: () => (pollRef.current ? pollRef.current.refresh() : Promise.resolve(tickRef.current?.())),
  }), []);
}
